import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Upload, Loader2, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { env } from "../../lib/env";
import { reconstructPhoto, ingestReconstruction, type ReconstructionResult, type ReconResponse } from "../../lib/photoToPlan";
import type { ProjectGraph } from "../../types/ontology";

/**
 * W3 photo → 2D plan + 3D scene affordance in the Design surface.
 *
 * Sits next to the catalog drawer. The designer picks a room photo,
 * the cockpit kicks the recon chain (VGGT → RoomFormer → Grounding-DINO
 * + SAM-2 → metric anchor), and the result lands as IN_ROOM placements
 * via the same path drag-drop uses.
 *
 * Confidence floor enforces VITE_INTAKE_CONFIDENCE_FLOOR — a low-confidence
 * recon surfaces the typed clarifying card instead of silently merging.
 */
interface Props {
  readonly graph: ProjectGraph;
  readonly projectId: string;
  readonly selectedRoomId: string | null;
  readonly selectedRoomLabel: string | null;
  readonly onMutated: () => void;
}

type Status = "idle" | "running" | "result" | "error";

export function ScanRoomCard({ graph, projectId, selectedRoomId, selectedRoomLabel, onMutated }: Props): JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);
  const [primary, setPrimary] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [result, setResult] = useState<ReconstructionResult | null>(null);
  const [ingested, setIngested] = useState<{ added: number; skipped: number; lowConfidence: number } | null>(null);

  // Build a category → catalog SKU id map once. The catalog is the
  // canonical inventory; recon results land as placements of these.
  const catalogByCategory = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of graph.nodes) {
      if (n.kind !== "SKU") continue;
      const cat = typeof n.attrs.category === "string" ? n.attrs.category : "";
      if (cat && !m.has(cat)) m.set(cat, n.id);
    }
    return m;
  }, [graph]);

  function reset(): void {
    setPrimary(null);
    setPreviewUrl(null);
    setStatus("idle");
    setErrorMsg(null);
    setResult(null);
    setIngested(null);
  }

  function onPickFile(ev: React.ChangeEvent<HTMLInputElement>): void {
    const f = ev.target.files?.[0];
    if (!f) return;
    setPrimary(f);
    setPreviewUrl(URL.createObjectURL(f));
    setStatus("idle");
    setErrorMsg(null);
    setResult(null);
    setIngested(null);
  }

  async function onScan(): Promise<void> {
    if (!primary) return;
    setStatus("running");
    setErrorMsg(null);
    const resp: ReconResponse = await reconstructPhoto({ primary });
    if ("error" in resp) {
      setStatus("error");
      setErrorMsg(`${resp.error}${resp.hint ? ` — ${resp.hint}` : ""}`);
      return;
    }
    if (resp.status === "pending") {
      // Backend returned a ComfyUI prompt_id. The /api/recon/result/:id
      // poll path lands in a follow-up; for V1 we surface that the
      // recon is queued so the user isn't left guessing.
      setStatus("error");
      setErrorMsg(`Recon queued (prompt ${resp.prompt_id.slice(0, 8)}). The result-poll endpoint binds with the cluster route.`);
      return;
    }
    setResult(resp.result);
    setStatus("result");
  }

  function onIngest(): void {
    if (!result || !selectedRoomId) return;
    const ing = ingestReconstruction({
      projectId,
      roomId: selectedRoomId,
      result,
      catalogSkuByCategory: catalogByCategory,
      confidenceFloor: env.VITE_INTAKE_CONFIDENCE_FLOOR,
    });
    setIngested(ing);
    onMutated();
  }

  const lowConfRoom = result && result.room.confidence < env.VITE_INTAKE_CONFIDENCE_FLOOR;

  return (
    <div className="rounded-xl border border-border bg-cream/70 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-ink-500">
          <Camera className="w-3 h-3" /> Scan a room (W3)
        </span>
        <span className="text-[10px] text-ink-500">
          vggt + roomformer + grounding-dino + sam-2
        </span>
      </div>

      {!primary ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-border rounded-lg py-4 text-[12px] text-ink-700 hover:border-foyr/40 hover:bg-foyr/5 transition"
        >
          <Upload className="w-4 h-4 text-foyr" /> Click a room photo to reconstruct a 2D plan + 3D scene
        </button>
      ) : (
        <div className="flex items-start gap-3">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="w-24 h-24 rounded-lg object-cover border border-border" />
          ) : null}
          <div className="flex-1 text-[12px] text-ink-700 space-y-1.5">
            <div className="font-mono text-ink-950 truncate" title={primary.name}>{primary.name}</div>
            <div className="text-[11px] text-ink-500">
              Target room:{" "}
              {selectedRoomLabel ? (
                <span className="text-foyr-deep">{selectedRoomLabel}</span>
              ) : (
                <span className="text-foyr">select a room above first</span>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onScan}
                disabled={status === "running"}
                className="btn btn-foyr !text-xs !py-1.5"
              >
                {status === "running" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                {status === "running" ? "Reconstructing…" : "Reconstruct"}
              </button>
              <button type="button" onClick={reset} className="btn btn-outline !text-xs !py-1.5">
                <X className="w-3 h-3" /> clear
              </button>
            </div>
          </div>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPickFile}
      />

      <AnimatePresence>
        {status === "running" ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 text-[11px] text-ink-500 space-y-1 overflow-hidden"
          >
            <div className="flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin text-foyr" /> 1 · VGGT dense depth + camera pose</div>
            <div className="flex items-center gap-2 opacity-60"><Loader2 className="w-3 h-3" /> 2 · RoomFormer floor-plan polygon</div>
            <div className="flex items-center gap-2 opacity-60"><Loader2 className="w-3 h-3" /> 3 · Grounding-DINO + SAM-2 SKU detection</div>
            <div className="flex items-center gap-2 opacity-60"><Loader2 className="w-3 h-3" /> 4 · Metric scale anchor (door height 2.03m)</div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {status === "error" && errorMsg ? (
        <div className="mt-3 text-[11.5px] text-foyr-deep border-l-2 border-foyr/40 pl-2">
          <span className="font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Recon failed:</span>{" "}
          {errorMsg}
        </div>
      ) : null}

      {status === "result" && result ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 text-[12px]">
            <CheckCircle2 className="w-3.5 h-3.5 text-foyr" />
            <span className="text-ink-950 font-medium">
              {result.room.roomKind} · {result.room.polygonM.length}-vertex polygon · {result.skus.length} SKUs detected
            </span>
            <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">
              room conf {(result.room.confidence * 100).toFixed(0)}%
            </span>
          </div>
          {lowConfRoom ? (
            <div className="text-[11px] text-foyr-deep border-l-2 border-foyr/40 pl-2">
              Room confidence below {(env.VITE_INTAKE_CONFIDENCE_FLOOR * 100).toFixed(0)}% floor —
              the recon agent recommends a second photo from a different angle.
            </div>
          ) : null}
          <div className="flex flex-wrap gap-1.5">
            {result.skus.map((s, i) => (
              <span
                key={i}
                className="pill bg-cream-dark border border-border text-ink-700 text-[10px] font-mono"
                title={`yaw ${s.yawDeg.toFixed(0)}° · conf ${(s.confidence * 100).toFixed(0)}%`}
              >
                {s.category}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onIngest}
              disabled={!selectedRoomId || lowConfRoom === true}
              className="btn btn-foyr !text-xs !py-1.5"
              title={!selectedRoomId ? "Select a room first" : ""}
            >
              <CheckCircle2 className="w-3 h-3" /> Ingest into {selectedRoomLabel ?? "room"}
            </button>
            {ingested ? (
              <span className="text-[11px] text-ink-700">
                +{ingested.added} placed · {ingested.skipped} skipped (no catalog match) · {ingested.lowConfidence} below confidence floor
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
